import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { LoadingOutlined } from '@ant-design/icons';

import Input from 'antd/es/input';
import Dropdown from 'antd/es/dropdown';
import Avatar from 'antd/es/avatar';
import Spin from 'antd/es/spin';
import Tag from 'antd/es/tag';
import List from 'antd/es/list';

import featureToggles from 'config/featureToggles';

import SearchService from 'services/search';

import Icon from './Icon';
import { Text } from './Text';
import PageMask from './PageMask';

import { getInitials } from 'helpers/string';
import { useFeatureToggleItem } from './FeatureToggle';

const SearchResultsWrapper = styled.div`
  width: 100%;
  background: #fff;
`;

const ResultsList = styled(List)`
  padding: 4px;

  & .ant-list-header {
    padding: 4px;
    font-family: Inter;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 20px;
    color: #A4B0BA;
  }

  & .ant-list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 !important;
    padding: 4px !important;
    transition: background .3s ease;
    cursor: pointer;

    &:hover {
      background-color: #f5f5f5;
    }

    & .ant-avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background-color: #B9C2C9;
      color: #FFFFFF;
      font-family: Inter;
      font-style: normal;
      font-weight: normal;
      font-size: 12px;
      line-height: 20px;
    }
  }

  & .ant-list-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Inter;
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 24px;
    color: #A4B0BA;
    padding: 0;
    padding-bottom: 8px;
  }

  & .ant-list-empty-text {
    display: none;
  }
`;

const StyledItemName = styled(Text)`
  font-family: Inter;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 22px;
  color: #45494D;
  margin-left: 10px;
`;

const IndividualStatusTagStyled = styled(Tag)`
  text-transform: capitalize;
  box-sizing: border-box;
  border-radius: 4px;
  font-family: Inter;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;
  margin: 0;
`;

const SpinWrapper = styled.span`
  width: 100%;
  padding-right: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  font-family: Inter;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: #828C94;

  & .ant-spin {
    margin-right: 8px;
  }
`;

const PageSearchWrapper = styled.div`
  & input {
    width: 0;
    -webkit-transition: all .3s;
    -moz-transition: all .3s;
    transition: all .4s linear .1s;

    ${props => props.searchMode === true && `
      width: 200px;
    `}
  }

  & .ant-input-affix-wrapper {
    padding: 4px;
    background: #FFFFFF;
    border: 1px solid #DAE0E6;
    box-sizing: border-box;
    border-radius: 24px;
    box-shadown: unset;

    ${props => props.searchMode === true && `
      padding: 4px 11px;
      border: 1px solid #6BC9E3;
      box-shadow: 0px 0px 4px rgba(24, 144, 255, 0.5);
      border-radius: 4px;
    `}
  }

  & .ant-input-suffix {
    padding: 4px;
    margin-left: unset;
    cursor: pointer;
  }
`;

const searchService = new SearchService();

const PageSearch = ({ ...rest }) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState({ containers: [], trailers: [], employees: [] });
  const [searchMode, setSearchMode] = useState(false);
  const [value, setValue] = useState('');
  const [resultsVisible, setResultsVisible] = useState(false);

  const individualsFeatureToggle = useFeatureToggleItem(featureToggles.FEATURE_INDIVIDUALS);
  const showIndividuals = individualsFeatureToggle?.visible ?? true;

  const onSearchResultClicked = (resultType, item) => {
    if (item) {
      switch (resultType) {
        case 'employee':
          navigateTo(`/individuals/${item.employee_id}`);
          break;

        case 'container':
          navigateTo(`/containers/${item.container_id}`);
          break;

        case 'trailer':
          navigateTo(`/trailers/${item.container_id}`);
          break;

        default:
        // noop
      }
    }

    setValue('');
    setResultsVisible(false);
    setSearchMode(false);
  };

  const navigateTo = (to) => {
    if (to) {
      history.push(to);
    };
  };

  useEffect(() => {
    if (value.length >= 3) {
      const timeout = setTimeout(() => {
        setIsLoading(true);
        searchService.search({ query: value })
          .then(data => {
            if (!data) return;
            const searchResults = { containers: [], trailers: [], employees: [] };
            searchResults.containers = data.containers.filter(i => {
              return i.container_type === 'CONTAINER';
            });
            searchResults.trailers = data.containers.filter(i => {
              return i.container_type === 'TRAILER';
            });
            searchResults.employees = data.employees;
            setSearchResults(searchResults);
          })
          .catch(err => {
            console.log(err);
          })
          .finally(() => {
            setIsLoading(false);
          });
        setResultsVisible(true);
      }, 300);

      return () => clearTimeout(timeout);
    } else {
      setResultsVisible(false);
    }
  }, [value]);

  return (
    <>
      <PageSearchWrapper
        {...rest}
        searchMode={searchMode}
        onClick={() => {
          if (!searchMode) {
            setSearchMode(true);
          }
        }}
      >
        <Dropdown
          overlay={(
            <SearchResults
              onSearchResultClicked={onSearchResultClicked}
              containers={searchResults.containers}
              trailers={searchResults.trailers}
              employees={searchResults.employees}
              showIndividuals={showIndividuals}
              loading={isLoading}
            />
          )}
          overlayStyle={{
            minWidth: '250px',
          }}
          placement="bottomCenter"
          trigger="click"
          visible={resultsVisible && searchMode}
        >
          <Input
            placeholder={`Search Equipment${showIndividuals ? ', Individual' : ''}`}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            suffix={
              <Icon name="search" size={20} color="#A4B0BA" />
            }
            style={{
              zIndex: 1000,
            }}
          />
        </Dropdown>
      </PageSearchWrapper>
      <PageMask show={searchMode} onClick={() => {
        setSearchMode(false);
      }} />
    </>
  );
};

const SearchResults = ({ containers, trailers, employees, showIndividuals, loading, onSearchResultClicked }) => {
  const loader = (
    <SpinWrapper>
      <Spin indicator={<LoadingOutlined style={{ color: "#42B0D5", fontSize: '20px' }} />} />
      Loading...
    </SpinWrapper>
  );

  return (
    <SearchResultsWrapper>
      {showIndividuals && (
        <ResultsList
          split={false}
          gutter={0}
          header="Individuals"
          dataSource={loading ? [] : employees.slice(0, 10)}
          renderItem={item => (
            <List.Item onClick={onSearchResultClicked.bind(null, 'employee', item)}>
              <div>
                <Avatar shape="square">{getInitials(item.full_name)}</Avatar>
                <StyledItemName>{item.full_name}</StyledItemName>
              </div>
              <IndividualStatusTag isWorking={item.is_working === 1}>
                {item.is_working === 1 ? 'Working' : 'Off'}
              </IndividualStatusTag>
            </List.Item>
          )}
          footer={
            loading ? loader : (
              !employees.length && 'No individual results'
            )
          }
        />
      )}
      <ResultsList
        split={false}
        gutter={0}
        header="Container"
        dataSource={loading ? [] : containers.slice(0, 10)}
        renderItem={item => (
          <List.Item onClick={onSearchResultClicked.bind(null, 'container', item)}>
            <div>
              <Avatar shape="square" icon={<Icon name="package" size={[19, 16]} />} />
              <StyledItemName>{item.code}</StyledItemName>
            </div>
          </List.Item>
        )}
        footer={
          loading ? loader : (
            !containers.length && 'No container results'
          )
        }
      />
      <ResultsList
        split={false}
        gutter={0}
        header="Trailer"
        dataSource={loading ? [] : trailers.slice(0, 10)}
        renderItem={item => (
          <List.Item onClick={onSearchResultClicked.bind(null, 'trailer', item)}>
            <div>
              <Avatar shape="square" icon={<Icon name="package" size={[19, 16]} />} />
              <StyledItemName>{item.code}</StyledItemName>
            </div>
          </List.Item>
        )}
        footer={
          loading ? loader : (
            !trailers.length && 'No trailer results'
          )
        }
      />
    </SearchResultsWrapper>
  );
};

const IndividualStatusTag = ({ isWorking, children }) => {
  if (isWorking) {
    return (
      <IndividualStatusTagStyled style={{
        background: '#F6FFED',
        border: '1px solid #B7EB8F',
        color: '#52C41A',
      }}>
        {children}
      </IndividualStatusTagStyled>
    );
  } else {
    return (
      <IndividualStatusTagStyled style={{
        color: '#595959',
        background: '#EFF2F5',
        border: '1px solid #DAE0E6',
      }}>
        {children}
      </IndividualStatusTagStyled>
    );
  }
};

export default PageSearch;
